/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Divider, IconButton, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import Card from 'bundles/browse/components/ProductCard/Card';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { DownloadItem } from 'bundles/enterprise-admin-analytics/components/DownloadItem';
import { REPORT_STEPS } from 'bundles/enterprise-admin-analytics/components/DrawerPollingSteps';
import type { ReportDownload } from 'bundles/enterprise-admin-analytics/components/ReportsStatusProvider';
import { REPORT_TYPE_FOR_TRACKING } from 'bundles/enterprise-admin-analytics/constants/reportsv2';
import { useReportProgressUpdater } from 'bundles/enterprise-admin-analytics/hooks/useReportProgressUpdater';
import { useReportsPolling } from 'bundles/enterprise-admin-analytics/hooks/useReportsPolling';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';

import _t from 'i18n!nls/enterprise-admin-analytics';

import { useReportsStatusActions, useReportsStatusState } from './ReportsStatusProvider';

const styles = {
  container: css`
    position: fixed;
    bottom: 60px;
    right: var(--cds-spacing-400);
    width: 441px;
    z-index: 10;
  `,
  card: css`
    padding: var(--cds-spacing-300);
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-200);
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-200);
  `,
  header: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `,
  downloadsContainer: css`
    & > :not(:last-child) {
      margin-bottom: var(--cds-spacing-200);
    }

    & > :last-child {
      margin-bottom: var(--cds-spacing-100);
    }

    margin-top: var(--cds-spacing-200);
    max-height: 280px;
    overflow-y: auto;
  `,

  buttonGroup: css`
    display: flex;
    gap: var(--cds-spacing-50);
  `,
  iconButton: css`
    color: var(--cds-color-neutral-primary);
  `,
  divider: css`
    color: var(--cds-color-neutral-stroke-primary-weak);
  `,
};

const ReportsStatusPanel = () => {
  const { downloads } = useReportsStatusState();
  const { updateDownload, clearDownloads } = useReportsStatusActions();
  const [isMinimized, setIsMinimized] = React.useState(false);
  const track = useTracker();
  const downloadsRef = React.useRef(downloads);
  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();

  // Keep ref in sync with downloads
  React.useEffect(() => {
    downloadsRef.current = downloads;
  }, [downloads]);

  // Track when user leaves the page (refresh/tab close) while downloads are in progress
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      const currentDownloads = downloadsRef.current;
      if (currentDownloads.length > 0) {
        track('dismiss_report_progress_popup', {
          actionType: 'exit_page',
          isDownloadsInProgress: currentDownloads.some((download) => download.status === REPORT_STEPS.LOADING),
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [track]);

  // // Update progress independently on a consistent interval
  useReportProgressUpdater({ downloads, updateDownload });

  // Track when report download completes or fails
  const handleDownloadStatusChange = React.useCallback<(download: ReportDownload, status: REPORT_STEPS) => void>(
    (download, status) => {
      track('download_enterprise_report', {
        ...enterpriseAdminTrackingData,
        reportType: { type: REPORT_TYPE_FOR_TRACKING[download.reportType] },
        reportDownloadStatus: status === REPORT_STEPS.SUCCESS ? 'success' : 'failed',
      });
    },
    [enterpriseAdminTrackingData, track]
  );

  // Polls backend for report status
  useReportsPolling({
    downloads,
    updateDownload,
    isEnabled: downloads.length > 0,
    onDownloadComplete: handleDownloadStatusChange,
  });

  const handleClose = React.useCallback<() => void>(() => {
    clearDownloads();
    setIsMinimized(false);
    track('dismiss_report_progress_popup', {
      actionType: 'dismiss_button',
      isDownloadsInProgress: downloads.some((download) => download.status === REPORT_STEPS.LOADING),
    });
  }, [clearDownloads, track, downloads]);

  const handleToggleMinimize = React.useCallback<() => void>(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  if (downloads.length === 0) {
    return null;
  }

  return (
    <ErrorBoundaryWithTagging componentName="ReportsStatusPanel">
      <div
        css={styles.container}
        role="region"
        aria-label={_t('Report generation status')}
        aria-live="polite"
        aria-atomic="true"
      >
        <Card css={styles.card}>
          <div css={styles.header}>
            <div>
              <Typography2 component="h3" variant="subtitleMedium" color="supportText">
                {_t('Reports')} ({downloads.length})
              </Typography2>
            </div>

            <div css={styles.buttonGroup}>
              <IconButton
                aria-label={isMinimized ? _t('Expand') : _t('Minimize')}
                tooltip={isMinimized ? _t('Expand') : _t('Minimize')}
                variant="ghost"
                size="small"
                icon={isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
                onClick={handleToggleMinimize}
                css={styles.iconButton}
              />
              <IconButton
                aria-label={_t('Close')}
                tooltip={_t('Close')}
                variant="ghost"
                size="small"
                icon={<CloseIcon />}
                onClick={handleClose}
                css={styles.iconButton}
              />
            </div>
          </div>
          {!isMinimized && (
            <div css={styles.downloadsContainer} role="list" aria-labelledby="reports-status-heading">
              {downloads.map((download, index) => (
                <React.Fragment key={download.id}>
                  <DownloadItem download={download} />
                  {index < downloads.length - 1 && <Divider css={styles.divider} />}
                </React.Fragment>
              ))}
            </div>
          )}
        </Card>
      </div>
    </ErrorBoundaryWithTagging>
  );
};

export default ReportsStatusPanel;
